import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LuCornerDownLeft } from "react-icons/lu";
import { FiTrash, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Checkbox, Button, Space } from "antd";
import { ThumbsUp, ThumbsDown, MessageCircle } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { showApiError } from "@/utils";
import apiClient from "@/services/apiClient";
import { BACKEND_URL } from "@/config";
import "./feedback.css";

const DEFAULT_FEEDBACK_OPTIONS = [
  "Not relevant",
  "Hard to understand",
  "Outdated information",
  "Other",
];

const ContentBriefFeedback = ({
  content_brief_id,
  feedbacks,
  setFeedbacks,
  sidebarOpen: controlledSidebarOpen,
  setSidebarOpen: controlledSetSidebarOpen,
}) => {
  const { t } = useTranslation();
  const [selectionInfo, setSelectionInfo] = useState(null);
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [internalSidebarOpen, setInternalSidebarOpen] = useState(false);
  const sidebarOpen =
    controlledSidebarOpen !== undefined
      ? controlledSidebarOpen
      : internalSidebarOpen;
  const setSidebarOpen = controlledSetSidebarOpen ?? setInternalSidebarOpen;
  const [deletingFeedbackId, setDeletingFeedbackId] = useState(null);
  const [feedbackOptions, setFeedbackOptions] = useState([]);
  const [customOption, setCustomOption] = useState(""); // Add this state

  const getLocalizedOption = (option) => {
    if (!option) return "";
    const key = option.replace(/[^a-zA-Z0-9]/g, "");
    return t(`contentOptimizer.feedbackOptions.${key}`, option);
  };

  useEffect(() => {
    apiClient
      .get(`${BACKEND_URL}/api/v1/optimize_content/feedback_impressions`)
      .then((res) => setFeedbackOptions(res.data?.content_brief || DEFAULT_FEEDBACK_OPTIONS))
      .catch(() => setFeedbackOptions(DEFAULT_FEEDBACK_OPTIONS));
  }, []);

  // Helper to calculate position
  const calculatePosition = (rect, showFeedbackSection) => {
    let x = rect.x + rect.width / 2;
    let y = rect.y - 10;
    const popupWidth = 180;
    const popupHeight = showFeedbackSection ? 180 : 50;
    if (x + popupWidth / 2 > window.innerWidth)
      x = window.innerWidth - popupWidth / 2 - 8;
    if (x - popupWidth / 2 < 0) x = popupWidth / 2 + 8;
    if (y - popupHeight < 0) y = rect.y + rect.height + 10 + popupHeight;
    if (y + popupHeight > window.innerHeight)
      y = window.innerHeight - popupHeight - 8;
    return { x, y };
  };

  useEffect(() => {
    const handleMouseUp = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();
      if (selection.rangeCount > 0 && text.length > 0) {
        const range = selection.getRangeAt(0);
        const commonAncestor = range.commonAncestorContainer;
        let element =
          commonAncestor.nodeType === 1
            ? commonAncestor
            : commonAncestor.parentElement;
        let found = false;
        while (element) {
          if (
            element.classList &&
            Array.from(element.classList).some((cls) =>
              cls.startsWith("feedback-"),
            )
          ) {
            found = true;
            break;
          }
          element = element.parentElement;
        }
        if (found) {
          const rect = range.getBoundingClientRect();
          const { x, y } = calculatePosition(rect, showFeedbackSection);
          setSelectionInfo({ x, y, text });
          return;
        }
      }
    };

    const handleClick = (e) => {
      if (selectionInfo && e.target.closest(".action-bar") === null) {
        setSelectionInfo(null);
      }
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [selectionInfo, showFeedbackSection]);

  const handleLike = () => {
    handleSubmitFeedback(["Positive"]);
  };

  const handleDislike = () => {
    handleSubmitFeedback(["Negative"]);
  };

  const handleSubmitFeedback = (impressions) => {
    if (feedbacks.length === 0) {
      setSidebarOpen(true);
    }

    let finalImpressions = impressions;
    if (impressions.includes("Other") && customOption.trim()) {
      finalImpressions = impressions
        .filter((opt) => opt !== "Other")
        .concat(customOption.trim());
    }
    const feedback = {
      content_brief_id,
      feedback_id: uuidv4(),
      text: selectionInfo.text,
      impressions: finalImpressions,
    };

    setFeedbacks((prev) => [...prev, feedback]);

    apiClient
      .put(`${BACKEND_URL}/api/v1/optimize_content/content-brief-feedback`, {
        feedbacks: [feedback],
      })
      .catch((err) => {
        console.error("Error submitting feedback:", err);
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "contentOptimizer.submitFeedbackError",
              "Failed to submit feedback. Please try again.",
            ),
        );
      })
      .finally(() => {
        setSelectionInfo(null);
        setShowFeedbackSection(false);
        setSelectedOptions([]);
        setCustomOption(""); // Reset custom option
      });
  };

  const handleDeleteFeedback = (feedback_id) => {
    setDeletingFeedbackId(feedback_id);
    apiClient
      .delete(
        `${BACKEND_URL}/api/v1/optimize_content/content-brief-feedback/${feedback_id}`,
      )
      .then(() => {
        setFeedbacks((prev) =>
          prev.filter((fb) => fb.feedback_id !== feedback_id),
        );
      })
      .catch((err) => {
        console.error("Error deleting feedback:", err);
        showApiError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t(
              "contentOptimizer.deleteFeedbackError",
              "Failed to delete feedback. Please try again.",
            ),
        );
      })
      .finally(() => {
        setDeletingFeedbackId(null);
      });
  };

  return (
    <>
      {selectionInfo && (
        <div
          className="action-bar"
          data-testid="feedback-action-bar"
          style={{
            top: selectionInfo.y,
            left: selectionInfo.x,
          }}
        >
          <div
            className="action-bar-buttons"
            style={showFeedbackSection ? { display: "none" } : {}}
          >
            <Space size={4} className="action-bar-space">
              <Button
                icon={<ThumbsUp size={22} />}
                onClick={handleLike}
                type="text"
                className="action-bar-btn"
                data-testid="feedback-action-bar-like-button"
              />
              <Button
                icon={<ThumbsDown size={22} />}
                onClick={handleDislike}
                type="text"
                className="action-bar-btn"
              />
              <Button
                icon={<MessageCircle size={22} />}
                onClick={() => setShowFeedbackSection(true)}
                type="text"
                className="action-bar-btn"
              />
            </Space>
          </div>
          {showFeedbackSection && (
            <div>
              <div className="feedback-section-title">
                {t("contentOptimizer.feedbackBubbleTitle", "What do you think about the selected text?")}
              </div>
              <Checkbox.Group
                className="feedback-section"
                value={selectedOptions}
                onChange={setSelectedOptions}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {feedbackOptions.map((option) => (
                  <Checkbox key={option} value={option}>
                    {getLocalizedOption(option)}
                  </Checkbox>
                ))}
                <Checkbox
                  value="Other"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: 4,
                  }}
                >
                  {t("contentOptimizer.feedbackOptions.Other", "Other")}
                  {selectedOptions.includes("Other") && (
                    <input
                      type="text"
                      placeholder={t("contentOptimizer.pleaseSpecify", "Please specify")}
                      value={customOption}
                      onChange={(e) => setCustomOption(e.target.value)}
                      style={{
                        marginLeft: 8,
                        padding: 2,
                        fontSize: 12,
                        borderRadius: 4,
                        border: "1px solid var(--border)",
                        width: 120,
                      }}
                    />
                  )}
                </Checkbox>
              </Checkbox.Group>
              <div className="feedback-section-actions">
                <Button
                  onClick={() => handleSubmitFeedback(selectedOptions)}
                  disabled={
                    selectedOptions.length === 0 ||
                    (selectedOptions.includes("Other") &&
                      customOption.trim() === "")
                  }
                  className="feedback-section-submit"
                >
                  {t("contentOptimizer.submitFeedback", "Submit")}
                </Button>
                <Button
                  icon={<LuCornerDownLeft size={16} />}
                  onClick={() => setShowFeedbackSection(false)}
                  className="feedback-section-back"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {/* Collapsible right sidebar */}
      <div className={`feedback-sidebar${sidebarOpen ? "" : " collapsed"}`}>
        <button
          onClick={() => setSidebarOpen((open) => !open)}
          className="feedback-toggle-btn"
          data-testid="feedback-toggle-button"
        >
          {sidebarOpen ? (
            <FiChevronRight size={20} />
          ) : (
            <>
              <FiChevronLeft size={20} />
              <span>
                <small>{t("contentOptimizer.feedback", "Feedback")}</small>
              </span>
              <span className="feedback-indicator" data-testid="feedback-count">
                {feedbacks.length}
              </span>
            </>
          )}
        </button>
        {sidebarOpen && (
          <div className="feedback-list" data-testid="feedback-list-container">
            <div className="feedback-list-title">{t("contentOptimizer.feedback", "Feedback")}</div>
            {feedbacks.length === 0 ? (
              <div className="feedback-list-empty" data-testid="feedback-empty">
                {t("contentOptimizer.noFeedbackYet", "No feedback yet.")}
              </div>
            ) : (
              feedbacks.map((feedback, index) => (
                <div key={index} className="feedback-list-item">
                  <div>
                    <div className="feedback-list-item-text">
                      {feedback.text}
                    </div>
                    <ul className="feedback-list-item-impressions">
                      {feedback.impressions.map((imp, i) => (
                        <li
                          key={i}
                          className={
                            imp === "Positive"
                              ? "feedback-impression-positive"
                              : imp === "Negative"
                                ? "feedback-impression-negative"
                                : undefined
                          }
                        >
                          {getLocalizedOption(imp)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    className="feedback-list-item-delete"
                    onClick={() => handleDeleteFeedback(feedback.feedback_id)}
                    aria-label={t("contentOptimizer.deleteFeedback", "Delete feedback")}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      marginLeft: 12,
                    }}
                    disabled={deletingFeedbackId === feedback.feedback_id}
                  >
                    <FiTrash size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ContentBriefFeedback;
